import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Layout from '../../components/ui/Layout';
import Table from '../../components/ui/Table';
import Modal from '../../components/ui/Modal';
import ValidatedInput from '../../components/ui/ValidatedInput';
import ValidatedTextarea from '../../components/ui/ValidatedTextarea';
import codeGeneratorService from '../../services/codeGeneratorService';
// @ts-ignore - ActionDropdown component exists but may have type issues
import ActionDropdown from '../../components/ui/ActionDropdown';
import ActionButtonsRow from '../../components/ui/ActionButtonsRow';
import SearchFilterCard, { FilterConfig } from '../../components/ui/SearchFilterCard';
import BulkActionsBar from '../../components/ui/BulkActionsBar';
import { RiEditLine, RiDeleteBinLine, RiEyeLine, RiPriceTag3Line, RiDeleteBin2Line, RiFileDownloadLine, RiErrorWarningLine, RiInformationLine, RiHistoryLine } from 'react-icons/ri';
import brandService, { Brand, BrandForm } from '../../services/brandService';
import { showAlert, showConfirm } from '../../utils/alert';
import { mapFieldErrors, focusFirstInvalidField, getApiErrorMessageAsync } from '../../utils/mapFieldErrors';
import { checkApiHealth } from '../../utils/apiWithLogging';
import { queryKeys } from '../../queryClient';
import { useCreateBrand, useUpdateBrand, useDeleteBrand } from '../../hooks/mutations/useBrandMutations';
import { useMutationWithRefresh } from '../../hooks/useMutationWithRefresh';
import { PAGINATION_CONFIG } from '../../config/pagination';
import Pagination from '../../components/ui/Pagination';
import { useUserPageSize } from '../../hooks/useUserPageSize';
import { formatDateTimeFull, getLocalDateString } from '../../utils/dateUtils';
import { downloadExcelFile } from '../../utils/downloadExcel';
import { CanCreate, CanEdit, CanDelete } from '../../components/auth/PermissionGuard';
import { usePermission } from '../../hooks/usePermission';
import LoadingOverlayModal from '../../components/ui/LoadingOverlayModal';
import { useClampPage } from '../../hooks/useClampPage';
import { runBulkDelete, bulkDeleteFailureMessage } from '../../utils/bulkDelete';

const BrandPage: React.FC = () => {
  const { canView, canCreate, canEdit, canDelete, canExport } = usePermission();
  // S1118 URL-sync: read URL params (Variant A overwrite + filter synced to URL for deep-link)
  const [searchParams, setSearchParams] = useSearchParams();
  const urlPageParam = searchParams.get('page');
  const urlStatusParam = searchParams.get('status') || '';

  // State สำหรับการค้นหาและ pagination
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(() => {
    if (!urlPageParam) return 1;
    const n = parseInt(urlPageParam, 10);
    if (!Number.isFinite(n) || n < 1) return 1;
    return Math.min(n, 9999);
  });
  const [pageSize, setPageSize] = useState<number>(PAGINATION_CONFIG.DEFAULT_PAGE_SIZE);
  useUserPageSize(pageSize, setPageSize);

  // State สำหรับ filter สถานะ (ส่งเป็น is_active param ให้ backend กรอง · synced to URL for deep-link UX)
  const [filterStatus, setFilterStatus] = useState<string>(
    urlStatusParam === 'active' || urlStatusParam === 'inactive' ? urlStatusParam : ''
  );

  // S1118 URL-sync: write filter+page to URL (Variant A overwrite + simple-ref reset)
  const syncFiltersToUrl = useCallback((status: string, page: number) => {
    const newParams = new URLSearchParams();
    if (status === 'active' || status === 'inactive') newParams.set('status', status);
    if (page > 1) newParams.set('page', String(page));
    setSearchParams(newParams, { replace: true });
  }, [setSearchParams]);

  useEffect(() => {
    syncFiltersToUrl(filterStatus, currentPage);
  }, [filterStatus, currentPage, syncFiltersToUrl]);

  // S1118: filter→page reset (simple string ref since filterStatus is single string · BrandPage filter setter doesn't always call setCurrentPage(1) inline)
  const prevFilterStatusRef = React.useRef(filterStatus);
  useEffect(() => {
    if (prevFilterStatusRef.current !== filterStatus) {
      prevFilterStatusRef.current = filterStatus;
      setCurrentPage(1);
    }
  }, [filterStatus]);
  
  // State สำหรับควบคุมการแสดง success message
  const [shouldShowSuccess, setShouldShowSuccess] = useState(true);
  
  // ใช้ hook พร้อมตัวเลือกไม่แสดง success message
  const createBrandMutation = useCreateBrand({ skipSuccessMessage: true });
  
  const updateBrandMutation = useUpdateBrand();
  const deleteBrandMutation = useDeleteBrand();
  
  // State สำหรับ Modal
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
  const [modalMode, setModalMode] = useState<'create' | 'edit' | 'view'>('create');
  
  // State สำหรับฟอร์ม
  const [formData, setFormData] = useState<BrandForm>({
    code: '',
    name: '',
    description: '',
    is_active: true,
  });
  
  // Simple validation state
  const [formErrors, setFormErrors] = useState<{[key: string]: string}>({});
  const [codeError, setCodeError] = useState<string>('');
  const [selectedBrands, setSelectedBrands] = useState<number[]>([]);
  const [bulkLoading, setBulkLoading] = useState<boolean>(false);
  
  // React Query for data fetching
  // BUG-S1520: กรอง is_active ที่ backend (server-side) แทน client-side filter บนข้อมูล paginated
  // — เดิมกรองเฉพาะหน้าปัจจุบัน ทำให้แถวที่ตรง filter ในหน้าอื่นไม่แสดง + totalItems เพี้ยน
  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: [...queryKeys.brands.list(), { page: currentPage, limit: pageSize, search: searchTerm, status: filterStatus }],
    queryFn: async () => {
      const params = {
        page: currentPage,
        limit: pageSize,
        search: searchTerm || undefined,
        is_active: filterStatus === 'active' ? true : filterStatus === 'inactive' ? false : undefined,
      };
      return await brandService.getBrands(params);
    },
  });

  // ข้อมูลยี่ห้อจาก React Query (backend กรองสถานะให้แล้ว)
  const brands = data?.items || [];
  const totalItems = data?.total || 0;
  useClampPage(currentPage, data?.total, pageSize, setCurrentPage, isLoading);

  // ฟังก์ชันสำหรับการเลือกยี่ห้อทั้งหมด
  // KD3 S2515: เปลี่ยนตัวกรอง/หน้าแล้วต้องล้างการเลือก — ไม่งั้นแถบ "เลือกแล้ว N" ค้าง และ "ลบที่เลือก" ลบแถวที่ผู้ใช้มองไม่เห็นแล้ว
  useEffect(() => {
    setSelectedBrands([]);
  }, [currentPage, pageSize, searchTerm, filterStatus]);

  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      setSelectedBrands(brands.map(brand => brand.id));
    } else {
      setSelectedBrands([]);
    }
  };
  
  // ฟังก์ชันสำหรับการเลือกยี่ห้อแต่ละรายการ
  const handleSelectBrand = (brandId: number, selected: boolean) => {
    if (selected) {
      setSelectedBrands(prev => [...prev, brandId]);
    } else {
      setSelectedBrands(prev => prev.filter(id => id !== brandId));
    }
  };
  
  // ฟังก์ชันสำหรับการลบยี่ห้อหลายรายการ
  const handleBulkDelete = async () => {
    if (selectedBrands.length === 0) {
      showAlert('เลือกข้อมูล', 'กรุณาเลือกยี่ห้อที่ต้องการลบ', 'info');
      return;
    }

    showConfirm(
      'ยืนยันการลบยี่ห้อ',
      `คุณต้องการลบยี่ห้อ ${selectedBrands.length} รายการที่เลือก ใช่หรือไม่? การกระทำนี้ไม่สามารถเปลี่ยนกลับได้`,
      async () => {
        setBulkLoading(true);
        try {
          // BUG-S2103-002: allSettled — รายการที่ลบไม่ได้ต้องบอกเหตุผล · ตารางต้อง refetch · เลือกค้างเฉพาะที่ลบไม่ได้
          const result = await runBulkDelete(selectedBrands, id => brandService.deleteBrand(id));
          setSelectedBrands(result.failed.map(f => f.id));
          refetch();
          if (result.failed.length === 0) {
            showAlert('สำเร็จ', `ลบยี่ห้อ ${result.succeeded.length} รายการสำเร็จ`, 'success');
          } else {
            showAlert(result.succeeded.length ? 'ลบได้บางรายการ' : 'ลบไม่สำเร็จ', bulkDeleteFailureMessage('ยี่ห้อ', result), result.succeeded.length ? 'warning' : 'error');
          }
        } catch (error) {
          console.error('Error bulk deleting:', error);
          showAlert('เกิดข้อผิดพลาด', 'ไม่สามารถลบรายการที่เลือกได้', 'error');
        } finally {
          setBulkLoading(false);
        }
      },
      { isDanger: true, confirmText: 'ลบ', cancelText: 'ยกเลิก' }
    );
  };

  // ฟังก์ชันสำหรับ Refresh
  const handleRefresh = () => {
    setSearchTerm('');
    setCurrentPage(1);
    setFilterStatus('');
    setSelectedBrands([]);
    refetch();
  };

  // State สำหรับ Export Loading
  const [isExporting, setIsExporting] = useState(false);

  // ฟังก์ชันสำหรับ Export Excel
  const handleExportExcel = async () => {
    setIsExporting(true);
    try {
      // ส่งตัวกรองชุดเดียวกับที่หน้ารายการใช้ — ไฟล์ต้องได้เฉพาะแถวที่เห็นบนจอ
      const blob = await brandService.exportToExcel({
        search: searchTerm || undefined,
        is_active: filterStatus === 'active' ? true : filterStatus === 'inactive' ? false : undefined,
      });
      downloadExcelFile(blob, `brands_${getLocalDateString()}.xlsx`);
      showAlert('สำเร็จ', 'ส่งออกข้อมูลเรียบร้อยแล้ว', 'success');
    } catch (error) {
      console.error('Error exporting data:', error);
      showAlert('เกิดข้อผิดพลาด', await getApiErrorMessageAsync(error, 'ไม่สามารถส่งออกข้อมูลได้'), 'error');
    } finally {
      setIsExporting(false);
    }
  };

  // Table columns configuration
  const columns = [
    {
      key: 'select',
      title: (
        <div className="flex items-center">
          <input
            type="checkbox"
            aria-label="เลือกยี่ห้อทั้งหมด"
            checked={selectedBrands.length === brands.length && brands.length > 0}
            onChange={(e) => handleSelectAll(e.target.checked)}
            className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
          />
        </div>
      ),
      width: '50px',
      render: (value: any, record: Brand) => (
        <input
          type="checkbox"
          aria-label={`เลือกยี่ห้อ ${record.name || record.id}`}
          checked={selectedBrands.includes(record.id)}
          onChange={(e) => handleSelectBrand(record.id, e.target.checked)}
          className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
        />
      ),
    },
    {
      key: 'code',
      title: 'รหัส',
      render: (value: string) => <span className="font-mono text-sm">{value || '-'}</span>,
      sortable: true,
      width: '120px'
    },
    {
      key: 'name',
      title: 'ชื่อยี่ห้อ',
      render: (value: string) => <span className="font-medium">{value || '-'}</span>,
      sortable: true
    },
    {
      key: 'description',
      title: 'คำอธิบาย',
      render: (value: string) => <span className="text-secondary-600">{value || '-'}</span>
    },
    {
      key: 'is_active',
      title: 'สถานะ',
      render: (value: boolean) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          value
            ? 'bg-success-100 text-success-800'
            : 'bg-danger-100 text-danger-800'
        }`}>
          {value ? 'ใช้งาน' : 'ไม่ใช้งาน'}
        </span>
      ),
      width: '100px'
    },
    {
      key: 'created_at',
      title: 'วันที่สร้าง',
      sortable: true,
      render: (value: string | null) => formatDateTimeFull(value)
    },
    {
      key: 'updated_at',
      title: 'อัพเดทล่าสุด',
      sortable: true,
      render: (value: string | null) => formatDateTimeFull(value)
    },
    {
      key: 'actions',
      title: 'การดำเนินการ',
      sticky: true,
      render: (_: any, record: Brand) => (
        <ActionDropdown
          actions={[
            ...(canView('brands') ? [{
              label: 'ดูรายละเอียด',
              icon: <RiEyeLine className="w-4 h-4" />,
              onClick: () => handleOpenModal('view', record)
            }] : []),
            ...(canEdit('brands') ? [{
              label: 'แก้ไข',
              icon: <RiEditLine className="w-4 h-4" />,
              onClick: () => handleOpenModal('edit', record)
            }] : []),
            ...(canDelete('brands') ? [{
              divider: true
            },
            {
              label: 'ลบ',
              icon: <RiDeleteBinLine className="w-4 h-4" />,
              onClick: () => handleDelete(record),
              className: 'text-danger-600 hover:text-danger-700 hover:bg-danger-50'
            }] : [])
          ]}
          buttonIcon={null}
          buttonLabel=""
        />
      ),
      width: '60px'
    }
  ];

  // จัดการการเปิด Modal
  const handleOpenModal = async (mode: 'create' | 'edit' | 'view', brand?: Brand) => {
    setModalMode(mode);
    setSelectedBrand(brand || null);
    
    if (mode === 'create') {
      // ใช้ preview รหัสถัดไปแทนการสร้างรหัสจริง
      let previewCode = '';
      try {
        const nextCodePreview = await codeGeneratorService.getNextCodePreview('brand', true);
        previewCode = nextCodePreview.next_code;
      } catch (error) {
        console.error('Error getting next code preview:', error);
        previewCode = ''; // fallback to empty
      }
      
      setFormData({
        code: previewCode,
        name: '',
        description: '',
        is_active: true,
      });
    } else if (brand) {
      setFormData({
        code: brand.code,
        name: brand.name,
        description: brand.description || '',
        is_active: brand.is_active === true || brand.is_active === 1 || brand.is_active === '1',
      });
    }
    
    setFormErrors({});
    setCodeError('');
    setShouldShowSuccess(true);
    setIsModalOpen(true);
  };

  // จัดการการปิด Modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedBrand(null);
    setFormErrors({});
    setCodeError('');
    // Reset form data เมื่อปิด modal
    setFormData({
      code: '',
      name: '',
      description: '',
      is_active: true,
    });
  };

  // จัดการการเปลี่ยนแปลงข้อมูลในฟอร์ม
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    // รหัสห้ามมีช่องว่าง — ตัดทิ้งตอนพิมพ์/วางจาก Excel/LINE ไม่งั้นโดนปฏิเสธทั้งที่ตาเห็นถูก (BUG-S2295-003)
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : (name === 'code' ? value.replace(/\s/g, '') : value)
    }));
    
    // Clear field error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // จัดการการบันทึกข้อมูล
  const handleSave = async () => {
    // Validate all fields
    const errors: {[key: string]: string} = {};
    
    if (!formData.code.trim()) {
      errors.code = 'กรุณาใส่รหัสยี่ห้อ';
    } else if (formData.code.length < 2 || formData.code.length > 50) {
      // BUG-S1206-001 fix: align FE limit with BE Pydantic validator (≤50)
      // Code field is currently auto-generated (disabled), so this is a latent drift fix
      // — prevents silent submit failures if code-template setting produces >20 chars
      errors.code = 'รหัสยี่ห้อต้องมีความยาว 2-50 ตัวอักษร';
    } else if (!/^[A-Za-z0-9_-]+$/.test(formData.code)) {
      errors.code = 'รหัสยี่ห้อต้องเป็นตัวอักษร ตัวเลข _ หรือ - เท่านั้น';
    }
    
    if (!formData.name.trim()) {
      errors.name = 'กรุณาใส่ชื่อยี่ห้อ';
    } else if (formData.name.length < 2 || formData.name.length > 200) {
      // BUG-S1204-001 fix: align FE limit with BE Pydantic validator (≤200) + DB varchar(200)
      errors.name = 'ชื่อยี่ห้อต้องมีความยาว 2-200 ตัวอักษร';
    }
    
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      // Scroll to first error field
      const firstErrorField = Object.keys(errors)[0];
      const errorElement = document.querySelector(`[name="${firstErrorField}"]`);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        (errorElement as HTMLInputElement).focus();
      }
      return;
    }
    
    // ตั้งค่าให้แสดง success message เป็นค่าเริ่มต้น
    setShouldShowSuccess(true);
    
    // ใช้ mutation hooks แทนการเรียก service โดยตรง
    if (modalMode === 'create') {
      createBrandMutation.mutate(formData, {
        onSuccess: () => {
          handleCloseModal();
          setCodeError('');
          // แสดง success message เฉพาะเมื่อไม่มีรหัสซ้ำ
          // KD3 S2121 (BUG-S2121-002): แสดงเสมอ — flag อ่านจาก closure ตอนกดบันทึก จึงค้าง false จากครั้งที่ล้มก่อนหน้า
          showAlert('สำเร็จ', 'สร้างยี่ห้อสำเร็จ', 'success');
        },
        onError: async (error: any) => {
          console.error('Error creating brand:', error);

          // BUG-S1162-001 PROPAGATION (S1163): server 422 field_errors → inline (WCAG 3.3.1)
          const fieldErrMap = mapFieldErrors(error);
          if (fieldErrMap) {
            setFormErrors(prev => ({ ...prev, ...fieldErrMap }));
            focusFirstInvalidField(fieldErrMap);
            setShouldShowSuccess(false);
            return;
          }

          // ตรวจสอบว่าเป็น error รหัสซ้ำหรือไม่
          const errorMessage = error.response?.data?.error?.user_message ||
                               error.response?.data?.error?.message ||
                               error.response?.data?.detail || '';

          if (errorMessage.includes('ชื่อ') && errorMessage.includes('มีอยู่ในระบบแล้ว')) {
            // OBS-S1109-001 FIX: BUSINESS_RULE_VIOLATION on name dup → inline field error
            setFormErrors(prev => ({ ...prev, name: errorMessage }));
            setShouldShowSuccess(false);
            // focus name field for immediate correction
            const nameEl = document.getElementsByName('name')[0] as HTMLInputElement | undefined;
            if (nameEl) nameEl.focus();
          } else if (errorMessage.includes('รหัส') && errorMessage.includes('มีอยู่ในระบบแล้ว')) {
            // ถ้าเป็นรหัสซ้ำ ให้สร้างรหัสใหม่
            const oldCode = formData.code;
            const newCode = await codeGeneratorService.generateSingleCode('brand');

            // อัพเดท state
            setFormData({...formData, code: newCode});

            // แสดงข้อความเตือน
            setCodeError(`รหัส ${oldCode} ไม่ว่างเนื่องจากมีการใช้งานแล้ว กรุณาบันทึกใหม่`);

            // ตั้งค่าให้ไม่แสดง success message
            setShouldShowSuccess(false);
          } else {
            // KD3 S2118 (BUG-S2118-001): toast ข้อความจริงมาจาก useMutationWithRefresh แล้ว — ไม่ซ้อน generic
            setShouldShowSuccess(false);
          }
        }
      });
    } else if (modalMode === 'edit' && selectedBrand) {
      updateBrandMutation.mutate(
        // BUG-S162-P1: send last-seen updated_at so a stale two-tab overwrite → 409 (not silent clobber)
        { id: selectedBrand.id, data: formData, ifMatch: (selectedBrand as any).updated_at || (selectedBrand as any).created_at },
        {
          onSuccess: () => {
            handleCloseModal();
            setCodeError('');
          },
          onError: (error: any) => {
            // P-S601-1 FIX (PO 2026-08-20): 409 stale-edit — refetch list ทันทีให้ token ใหม่
            if (error.response?.status === 409) {
              refetch(); // toast เฉพาะมาจาก global onError ของ hook แล้ว — เติมเฉพาะ refetch กัน 409 วน
              return;
            }
            // BUG-S1162-001 PROPAGATION (S1163): server 422 field_errors → inline (WCAG 3.3.1)
            const fieldErrMap = mapFieldErrors(error);
            if (fieldErrMap) {
              setFormErrors(prev => ({ ...prev, ...fieldErrMap }));
              focusFirstInvalidField(fieldErrMap);
              return;
            }

            // OBS-S1109-001 FIX: BUSINESS_RULE_VIOLATION on name dup → inline field error (Edit flow)
            const errorMessage = error.response?.data?.error?.user_message ||
                                 error.response?.data?.error?.message ||
                                 error.response?.data?.detail || '';
            if (errorMessage.includes('ชื่อ') && errorMessage.includes('มีอยู่ในระบบแล้ว')) {
              setFormErrors(prev => ({ ...prev, name: errorMessage }));
              const nameEl = document.getElementsByName('name')[0] as HTMLInputElement | undefined;
              if (nameEl) nameEl.focus();
            }
            // global toast will still fire from useMutationWithRefresh onError (kept for fallback)
          }
        }
      );
    }
  };

  // จัดการการลบข้อมูล
  const handleDelete = (brand: Brand) => {
    showConfirm(
      'ยืนยันการลบ',
      `คุณต้องการลบยี่ห้อ "${brand.name}" ใช่หรือไม่? การกระทำนี้ไม่สามารถเปลี่ยนกลับได้`,
      () => {
        deleteBrandMutation.mutate(brand.id);
      },
      { isDanger: true, confirmText: 'ลบ', cancelText: 'ยกเลิก' }
    );
  };

  // Loading state จาก mutations
  const loading = createBrandMutation.isPending ||
                 updateBrandMutation.isPending ||
                 deleteBrandMutation.isPending;

  // Filters configuration สำหรับ SearchFilterCard
  const filters: FilterConfig[] = [
    {
      key: 'status',
      label: 'สถานะ',
      type: 'select',
      options: [
        { value: 'active', label: 'ใช้งาน' },
        { value: 'inactive', label: 'ไม่ใช้งาน' },
      ],
      tagColor: 'success',
    },
  ];

  return (
    <Layout>
      <div className="space-y-6">
        {/* Page Header with Executive Navy Theme */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
          <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-primary-600 flex items-center justify-center flex-shrink-0 shadow-sm">
            <RiPriceTag3Line className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-secondary-900 leading-tight">แบรนด์</h1>
            <p className="text-xs text-secondary-500 mt-0.5">จัดการแบรนด์สินค้าและอุปกรณ์</p>
          </div>
        </div>
        {totalItems > 0 && (
          <span className="inline-flex items-center px-3 min-h-[44px] sm:min-h-[32px] py-1.5 rounded-lg bg-secondary-100 text-secondary-600 text-xs font-medium self-start sm:self-auto">
            {totalItems.toLocaleString()} รายการ
          </span>
        )}
      </div>

        {/* Error Message from React Query */}
        {error && (
          <div className="bg-danger-50 border border-danger-300 rounded-lg p-4">
            <div className="flex items-center">
              <RiErrorWarningLine className="w-5 h-5 text-danger-600 mr-2" />
              <span className="text-danger-800">เกิดข้อผิดพลาดในการโหลดข้อมูล</span>
            </div>
          </div>
        )}

        {/* Action Buttons Row */}
        <ActionButtonsRow
          onAdd={canCreate('brands') ? () => handleOpenModal('create') : undefined}
          onRefresh={handleRefresh}
          refreshLoading={isFetching}
          onExport={canExport('brands') ? handleExportExcel : undefined}
          exportDisabled={!brands?.length}
        />

        {/* Search & Filter Card */}
        <SearchFilterCard
          searchTerm={searchTerm}
          onSearchChange={(value) => {
            setSearchTerm(value);
            setCurrentPage(1);
          }}
          filters={filters}
          filterValues={{ status: filterStatus }}
          onFilterChange={(key, value) => {
            if (key === 'status') {
              setFilterStatus(value);
              setCurrentPage(1);
            }
          }}
          searchPlaceholder="ค้นหารหัสหรือชื่อยี่ห้อ..."
        />

        {/* Bulk Actions Bar */}
        {canDelete('brands') && (
          <BulkActionsBar
            selectedCount={selectedBrands.length}
            onBulkDelete={handleBulkDelete}
            onClearSelection={() => setSelectedBrands([])}
            loading={bulkLoading}
          />
        )}

        {/* Table */}
        <Table
          columns={canDelete('brands') ? columns : columns.filter(c => c.key !== 'select')}  /* KD3 S2122 (BUG-S2122-004): ไม่มีสิทธิ์ลบ = ไม่มีช่องติ๊ก (ติ๊กแล้วไม่มีอะไรให้ทำ) */
          data={brands}
          loading={isLoading}
          emptyText={error ? 'โหลดข้อมูลไม่สำเร็จ' : 'ไม่พบข้อมูลยี่ห้อ'}
          emptyHint={error ? null : undefined}  /* BUG-S2273-001: โหลดล้ม ≠ ตัวกรองไม่เจอ */
          pagination={false}
          searchable={false}
          actions={false}
          rowKey="id"
        />

        {/* Pagination */}
        {!isLoading && brands.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalItems={totalItems}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);
              setCurrentPage(1);
            }}
            showPageSizeSelector={true}
            showSummary={true}
            className="mt-5"
          />
        )}

        {/* Modal */}
        <Modal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          title={
            modalMode === 'create' ? 'เพิ่มยี่ห้อ' :
            modalMode === 'edit' ? 'แก้ไขยี่ห้อ' : 'ดูรายละเอียดยี่ห้อ'
          }
          size="lg"
        >
          <div className="space-y-4">
            {/* Section: ข้อมูลพื้นฐาน */}
            <div className="flex items-center gap-2 pb-2 border-b border-secondary-200">
              <div className="w-8 h-8 bg-primary-600 rounded-md flex items-center justify-center shadow-sm">
                <RiInformationLine className="w-4 h-4 text-white" />
              </div>
              <h3 className="text-base font-semibold text-secondary-800">ข้อมูลพื้นฐาน</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* รหัสยี่ห้อ - readonly ในทุกโหมด */}
              <div>
                <ValidatedInput
                  label="รหัสยี่ห้อ"
                  name="code"
                  value={formData.code}
                  onChange={handleFormChange}
                  error={formErrors.code}
                  required={true}
                  disabled={true}
                  placeholder="รหัสจะสร้างอัตโนมัติ"
                />
                {codeError && (
                  <p className="mt-1 text-sm text-danger-600 flex items-center">
                    <RiErrorWarningLine className="w-4 h-4 mr-1" />
                    {codeError}
                  </p>
                )}
              </div>
              <ValidatedInput
                label="ชื่อยี่ห้อ"
                name="name"
                value={formData.name}
                onChange={handleFormChange}
                error={formErrors.name}
                required={true}
                disabled={(modalMode as string) === 'view'}
                placeholder="เช่น Sunmi, Samsung"
                maxLength={200}
              />
            </div>

            <ValidatedTextarea
              label="คำอธิบาย"
              name="description"
              value={formData.description}
              onChange={handleFormChange}
              disabled={(modalMode as string) === 'view'}
              rows={3}
              placeholder="คำอธิบายเพิ่มเติมเกี่ยวกับยี่ห้อ"
            />
            
            {/* ฟิลด์สถานะ */}
            {modalMode !== 'view' && (
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  สถานะ
                </label>
                <select
                  aria-label="สถานะ"
                  name="is_active"
                  value={formData.is_active ? 'true' : 'false'}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.value === 'true' }))}
                  className="w-full px-3 py-2 border border-secondary-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
                  disabled={(modalMode as string) === 'view'}
                >
                  <option value="true">ใช้งาน</option>
                  <option value="false">ไม่ใช้งาน</option>
                </select>
              </div>
            )}

            {modalMode === 'view' && selectedBrand && (
              <>
                {/* สถานะและวันที่ */}
                <div className="border-t pt-4 mt-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-secondary-600">สถานะ:</span>
                      <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                        selectedBrand.is_active
                          ? 'bg-success-100 text-success-800'
                          : 'bg-danger-100 text-danger-800'
                      }`}>
                        {selectedBrand.is_active ? 'ใช้งาน' : 'ไม่ใช้งาน'}
                      </span>
                    </div>
                    <div>
                      <span className="text-secondary-600">วันที่สร้าง:</span>
                      <span className="ml-2">{formatDateTimeFull(selectedBrand.created_at)}</span>
                    </div>
                  </div>
                </div>

                {/* ข้อมูลผู้สร้างและผู้แก้ไข */}
                <div className="mt-6 p-4 border border-secondary-200 rounded-lg bg-secondary-50">
                  {/* Section Header with Icon Box Pattern */}
                  <div className="flex items-center gap-2 pb-2 mb-4 border-b border-secondary-200">
                    <div className="w-8 h-8 bg-info-600 rounded-md flex items-center justify-center shadow-sm">
                      <RiHistoryLine className="w-4 h-4 text-white" />
                    </div>
                    <h4 className="text-base font-semibold text-secondary-800">ข้อมูลการสร้างและแก้ไข</h4>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-secondary-600">สร้างโดย:</span>
                      <span className="ml-2 font-medium">
                        {selectedBrand.created_by?.full_name || selectedBrand.created_by?.username || '-'}
                      </span>
                    </div>
                    <div>
                      <span className="text-secondary-600">วันที่สร้าง:</span>
                      <span className="ml-2 font-medium">
                        {formatDateTimeFull(selectedBrand.created_at)}
                      </span>
                    </div>
                    {/* แสดงข้อมูลการแก้ไขเฉพาะเมื่อเคยถูกแก้ไขจริง */}
                    {selectedBrand.updated_at && selectedBrand.updated_by && (
                      <>
                        <div>
                          <span className="text-secondary-600">แก้ไขล่าสุดโดย:</span>
                          <span className="ml-2 font-medium">
                            {selectedBrand.updated_by?.full_name || selectedBrand.updated_by?.username || '-'}
                          </span>
                        </div>
                        <div>
                          <span className="text-secondary-600">วันที่แก้ไขล่าสุด:</span>
                          <span className="ml-2 font-medium">
                            {formatDateTimeFull(selectedBrand.updated_at)}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="flex flex-wrap justify-end gap-3 mt-6">
            <button
              onClick={handleCloseModal}
              className="px-4 py-2 text-secondary-700 bg-secondary-100 rounded-md hover:bg-secondary-200"
            >
              {modalMode === 'view' ? 'ปิด' : 'ยกเลิก'}
            </button>
            {modalMode !== 'view' && (
              <button
                onClick={handleSave}
                disabled={loading}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
              >
                {loading ? 'กำลังบันทึก...' : 'บันทึก'}
              </button>
            )}
          </div>
        </Modal>

        {/* Loading Overlay for Export */}
        <LoadingOverlayModal
          isOpen={isExporting}
          icon="export"
          title="กำลังส่งออกข้อมูล"
          submessage="กรุณารอสักครู่ ระบบกำลังเตรียมไฟล์ Excel"
        />
      </div>
    </Layout>
  );
};

export default BrandPage;